import { useState, useMemo, useEffect, useCallback } from "react";
import styles from "./TravelerRules.module.css";
import {
  useTravelerRules,
  useUpdateTravelerRules,
  useResetTravelerRules,
} from "../../../../api/people/usePeopleApi";

// ── UI metadata per traveler type ─────────────────────────────────────────
const AGE_RULE_META = {
  INF: {
    label: "Infant",
    shortCode: "INF",
    icon: "👶",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    locked: false,
  },
  CHD: {
    label: "Child",
    shortCode: "CHD",
    icon: "👦",
    color: "#d97706",
    bg: "#fff7ed",
    border: "#fed7aa",
    locked: false,
  },
  ADT: {
    label: "Adult",
    shortCode: "ADT",
    icon: "🧑",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
    locked: true,
  },
};

const DEFAULT_AGE_RULES = [
  {
    id: "INF",
    label: "Infant",
    shortCode: "INF",
    icon: "👶",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    minAge: 0,
    maxAge: 1,       
    description: "Under 2 years old on the date of the outbound flight",
    locked: false,
  },
  {
    id: "CHD",
    label: "Child",
    shortCode: "CHD",
    icon: "👦",
    color: "#d97706",
    bg: "#fff7ed",
    border: "#fed7aa",
    minAge: 2,
    maxAge: 11,
    description: "Between 2 and 11 years old on the date of the outbound flight",
    locked: false,
  },
  {
    id: "ADT",
    label: "Adult",
    shortCode: "ADT",
    icon: "🧑",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
    minAge: 12,
    maxAge: null,    // null = no upper limit
    description: "12 years or older on the date of the outbound flight",
    locked: true,    // Adult upper bound is always "no limit"
  },
];

const DEFAULT_DOCUMENT_RULES = [
  { id: "INF", label: "Infant 👶", passportRequired: false, passportRecommended: true,  minExpiryMonths: 6,  notes: "Many destinations do not require passports for infants under 1 year, but it is recommended." },
  { id: "CHD", label: "Child 👦",  passportRequired: true,  passportRecommended: true,  minExpiryMonths: 6,  notes: "Passport required for all international travel. Must be valid for at least 6 months beyond return date." },
  { id: "ADT", label: "Adult 🧑",  passportRequired: true,  passportRecommended: true,  minExpiryMonths: 6,  notes: "Passport required for all international travel. Must be valid for at least 6 months beyond return date." },
];

const DEFAULT_BOOKING_RULES = {
  maxInfantsPerAdult:        1,
  infantsMustHaveAdult:      true,
  maxChildrenUnaccompanied:  0,
  childrenMustHaveAdult:     true,
  minAdultsPerBooking:       1,
  maxTravelersPerBooking:    9,
  ageCalculationPoint:       "outbound",  
  ageVerificationRequired:   true,
  passportExpiryWarningDays: 180,
};

const calculateAge = (dob, referenceDate = new Date()) => {
  if (!dob) return null;
  const birth = new Date(dob);
  let age = referenceDate.getFullYear() - birth.getFullYear();
  const m = referenceDate.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && referenceDate.getDate() < birth.getDate())) age--;
  return age;
};

const getTypeFromAge = (age, rules) => {
  if (age === null || age < 0) return null;
  for (const rule of [...rules].sort((a, b) => (a.minAge ?? 0) - (b.minAge ?? 0))) {
    const withinMax = rule.maxAge === null || age <= rule.maxAge;
    if (age >= rule.minAge && withinMax) return rule;
  }
  return null;
};

const SectionHeader = ({ icon, title, desc, badge }) => (
  <div className={styles.sectionHeader}>
    <div className={styles.sectionHeaderLeft}>
      <div className={styles.sectionHeaderIcon}>{icon}</div>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {desc && <p className={styles.sectionDesc}>{desc}</p>}
      </div>
    </div>
    {badge && <span className={styles.sectionBadge}>{badge}</span>}
  </div>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <div className={styles.toggleWrap}>
    <input
      type="checkbox"
      id={`toggle-${Math.random()}`}
      checked={checked}
      onChange={e => !disabled && onChange(e.target.checked)}
      disabled={disabled}
      className={styles.toggleInput}
    />
    <label className={`${styles.toggleLabel} ${disabled ? styles.toggleDisabled : ""}`}>
      <span className={styles.toggleSwitch} />
    </label>
  </div>
);

// ── Map API rules to UI format ────────────────────────────────────────────
const mapApiToAgeRules = (apiRules) => {
  if (!Array.isArray(apiRules) || apiRules.length === 0) return DEFAULT_AGE_RULES;
  const order = ["INF", "CHD", "ADT"];
  return order.map((code) => {
    const r = apiRules.find((x) => x.code === code);
    const meta = AGE_RULE_META[code] ?? AGE_RULE_META.ADT;
    if (!r) {
      const def = DEFAULT_AGE_RULES.find((d) => d.id === code);
      return { ...meta, id: code, ...def };
    }
    const desc = r.description ?? (r.maxAge != null
      ? `Between ${r.minAge} and ${r.maxAge} years old on the date of the outbound flight`
      : `${r.minAge} years or older on the date of the outbound flight`);
    return {
      ...meta,
      id: r.code,
      minAge: r.minAge,
      maxAge: r.maxAge ?? null,
      description: desc,
      locked: code === "ADT",
    };
  });
};

// ── Map ageRules to API format ────────────────────────────────────────────
const mapAgeRulesToApi = (ageRules) => ({
  rules: ageRules.map((r) => ({
    code: r.id,
    minAge: r.minAge,
    maxAge: r.maxAge === "" || r.maxAge == null ? null : Number(r.maxAge),
    description: r.description,
    isActive: true,
  })),
});

const TravelerRules = () => {
  const { data: apiRules, loading, error, refetch } = useTravelerRules();
  const { execute: updateRules, loading: saving, error: updateError } = useUpdateTravelerRules();
  const { execute: resetRules, loading: resetting } = useResetTravelerRules();

  const [ageRules,      setAgeRules]      = useState(DEFAULT_AGE_RULES);
  const [docRules,      setDocRules]      = useState(DEFAULT_DOCUMENT_RULES);
  const [bookingRules,  setBookingRules]  = useState(DEFAULT_BOOKING_RULES);
  const [saved,         setSaved]         = useState(false);
  const [editingAge,    setEditingAge]    = useState(null);
  const [activeSection, setActiveSection] = useState("age");

  const [simDob,    setSimDob]    = useState("");
  const [simDate,   setSimDate]   = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    setAgeRules(mapApiToAgeRules(apiRules));
  }, [apiRules]);

  const simResult = useMemo(() => {
    if (!simDob) return null;
    const refDate = simDate ? new Date(simDate) : new Date();
    const age     = calculateAge(simDob, refDate);
    if (age === null || age < 0) return null;
    const type = getTypeFromAge(age, ageRules);
    return { age, type };
  }, [simDob, simDate, ageRules]);

  // ── Age Rule Editing ──
  const [editDraft, setEditDraft] = useState({});

  const startEditAge = (rule) => {
    setEditingAge(rule.id);
    setEditDraft({ minAge: rule.minAge, maxAge: rule.maxAge ?? "" });
  };

  const cancelEditAge = () => { setEditingAge(null); setEditDraft({}); };

  const saveEditAge = (id) => {
    setAgeRules(prev => prev.map(r => {
      if (r.id !== id) return r;
      return {
        ...r,
        minAge: Number(editDraft.minAge),
        maxAge: editDraft.maxAge === "" || editDraft.maxAge === null ? null : Number(editDraft.maxAge),
      };
    }));
    // Auto-update adjacent rules to stay consistent
    setAgeRules(prev => {
      const sorted = [...prev].sort((a,b) => a.minAge - b.minAge);
      return sorted.map((r, i) => ({
        ...r,
        description: r.maxAge !== null
          ? `Between ${r.minAge} and ${r.maxAge} years old on the date of the outbound flight`
          : `${r.minAge} years or older on the date of the outbound flight`,
      }));
    });
    setEditingAge(null);
  };

  // ── Document Rule Updates ──
  const updateDocRule = (id, field, value) => {
    setDocRules(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  // ── Booking Rule Updates ──
  const updateBookingRule = (field, value) => {
    setBookingRules(prev => ({ ...prev, [field]: value }));
  };

  // ── Save ──
  const handleSave = useCallback(async () => {
    try {
      await updateRules(mapAgeRulesToApi(ageRules));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      refetch();
    } catch (err) {
      console.error("Save rules failed:", err);
    }
  }, [ageRules, updateRules, refetch]);

  // ── Reset ──
  const handleReset = useCallback(async () => {
    if (!window.confirm("Reset all age classification rules to system defaults?")) return;
    try {
      await resetRules();
      refetch();
      setDocRules(DEFAULT_DOCUMENT_RULES);
      setBookingRules(DEFAULT_BOOKING_RULES);
    } catch (err) {
      console.error("Reset rules failed:", err);
    }
  }, [resetRules, refetch]);

  const SECTIONS = [
    { key: "age",      label: "Age Classification", icon: "🎂" },
    { key: "documents", label: "Document Rules",    icon: "🛂" },
    { key: "booking",  label: "Booking Constraints", icon: "📋" },
    { key: "simulator",label: "Age Simulator",      icon: "⚡" },
  ];

  return (
    <div className={styles.container}>

      {/* ── Error Banner ── */}
      {(error || updateError) && (
        <div className={styles.errorBanner}>
          {error || updateError}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>⚙️</div>
          <div>
            <h1 className={styles.headerTitle}>Traveler Rules</h1>
            <p className={styles.headerDesc}>
              Configure age classification thresholds, document requirements, and booking constraints
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.resetBtn} onClick={handleReset} disabled={loading || resetting}>
            {resetting ? "Resetting..." : "↩ Reset to Defaults"}
          </button>
          <button
            className={`${styles.saveBtn} ${saved ? styles.saveBtnSaved : ""}`}
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save All Rules"}
          </button>
        </div>
      </div>

      {/* ── Warning Banner ── */}
      <div className={styles.warningBanner}>
        <span className={styles.warningIcon}>⚠️</span>
        <div>
          <strong>Changes affect all future bookings.</strong> Modifying age thresholds will not retroactively
          reclassify travelers in existing bookings. Age is always re-evaluated at the time of booking creation
          based on the outbound flight date.
        </div>
      </div>

      {/* ── Section Nav ── */}
      <div className={styles.sectionNav}>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            className={`${styles.navBtn} ${activeSection === s.key ? styles.navBtnActive : ""}`}
            onClick={() => setActiveSection(s.key)}
          >
            <span>{s.icon}</span> {s.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section: Age Classification                                     */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "age" && (
        <div className={styles.sectionContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingIcon}>⏳</div>
              <p className={styles.loadingText}>Loading rules...</p>
            </div>
          ) : (
          <>
          <SectionHeader
            icon="🎂"
            title="Age Classification Rules"
            desc="Define the age ranges that determine Infant (INF), Child (CHD), and Adult (ADT) classification. Age is calculated from date of birth relative to the outbound flight date."
            badge="3 Types"
          />

          {/* ── Rule Cards ── */}
          <div className={styles.ageRulesList}>
            {ageRules.map((rule, idx) => {
              const isEditing = editingAge === rule.id;
              return (
                <div
                  key={rule.id}
                  className={`${styles.ageRuleCard} ${isEditing ? styles.ageRuleCardEditing : ""}`}
                  style={{ "--rcolor": rule.color, "--rbg": rule.bg, "--rborder": rule.border }}
                >
                  {/* ── Card Top ── */}
                  <div className={styles.ageRuleTop}>
                    <div className={styles.ageRuleLeft}>
                      <div className={styles.ageRuleBadge}
                        style={{ background: rule.bg, color: rule.color, border: `1px solid ${rule.border}` }}>
                        <span className={styles.ageRuleIcon}>{rule.icon}</span>
                        <span className={styles.ageRuleLabel}>{rule.label}</span>
                        <span className={styles.ageRuleCode}>{rule.shortCode}</span>
                      </div>
                      <p className={styles.ageRuleDesc}>{rule.description}</p>
                    </div>

                    {/* ── Range Display / Edit ── */}
                    <div className={styles.ageRuleRight}>
                      {!isEditing ? (
                        <div className={styles.ageRangeDisplay}>
                          <div className={styles.ageRangeBox}>
                            <span className={styles.ageRangeLabel}>Min Age</span>
                            <span className={styles.ageRangeValue}>{rule.minAge}</span>
                            <span className={styles.ageRangeUnit}>years</span>
                          </div>
                          <div className={styles.ageRangeArrow}>→</div>
                          <div className={styles.ageRangeBox}>
                            <span className={styles.ageRangeLabel}>Max Age</span>
                            <span className={styles.ageRangeValue}>
                              {rule.maxAge !== null ? rule.maxAge : "∞"}
                            </span>
                            <span className={styles.ageRangeUnit}>
                              {rule.maxAge !== null ? "years" : "no limit"}
                            </span>
                          </div>
                          {!rule.locked && (
                            <button
                              className={styles.ageEditBtn}
                              onClick={() => startEditAge(rule)}
                            >
                              Edit Range
                            </button>
                          )}
                          {rule.locked && (
                            <div className={styles.ageLocked}>🔒 Fixed</div>
                          )}
                        </div>
                      ) : (
                        <div className={styles.ageRangeEdit}>
                          <div className={styles.ageEditGroup}>
                            <label>Min Age</label>
                            <input
                              type="number"
                              min={0}
                              max={99}
                              value={editDraft.minAge}
                              onChange={e => setEditDraft(d => ({ ...d, minAge: e.target.value }))}
                              className={styles.ageInput}
                            />
                          </div>
                          <div className={styles.ageRangeArrow}>→</div>
                          <div className={styles.ageEditGroup}>
                            <label>Max Age</label>
                            <input
                              type="number"
                              min={0}
                              max={99}
                              placeholder="∞"
                              value={editDraft.maxAge ?? ""}
                              onChange={e => setEditDraft(d => ({ ...d, maxAge: e.target.value === "" ? null : e.target.value }))}
                              className={styles.ageInput}
                              disabled={rule.id === "ADT"}
                            />
                          </div>
                          <div className={styles.ageEditActions}>
                            <button className={styles.ageSaveBtn}   onClick={() => saveEditAge(rule.id)}>Save</button>
                            <button className={styles.ageCancelBtn} onClick={cancelEditAge}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Timeline Bar ── */}
                  <div className={styles.ageTimeline}>
                    <div className={styles.ageTimelineTrack}>
                      <div
                        className={styles.ageTimelineFill}
                        style={{
                          left: `${(rule.minAge / 18) * 100}%`,
                          width: rule.maxAge !== null
                            ? `${((rule.maxAge - rule.minAge + 1) / 18) * 100}%`
                            : `${((18 - rule.minAge) / 18) * 100}%`,
                          background: rule.color,
                          opacity: 0.25,
                        }}
                      />
                      {[0,2,5,8,11,12,15,18].map(yr => (
                        <div key={yr} className={styles.ageTimelineTick} style={{ left: `${(yr/18)*100}%` }}>
                          <span>{yr}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.ageTimelineLabels}>
                      <span>0</span>
                      <span>18+ yrs</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Age Calculation Reference Point ── */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <div className={styles.cardHeader}>
              <span>📅</span>
              <h3>Age Calculation Reference Point</h3>
            </div>
            <div className={styles.cardBody}>
              <p className={styles.cardDesc}>
                Choose the date used to calculate a traveler's age for type classification.
                This applies to all new bookings only — existing bookings are not affected.
              </p>
              <div className={styles.radioGroup}>
                {[
                  { value: "outbound",  label: "Outbound Flight Date", desc: "Age is calculated based on the first day of travel (industry standard)" },
                  { value: "booking",   label: "Booking Creation Date", desc: "Age is calculated at the moment the booking is created" },
                ].map(opt => (
                  <label key={opt.value} className={`${styles.radioCard} ${bookingRules.ageCalculationPoint === opt.value ? styles.radioCardActive : ""}`}>
                    <input
                      type="radio"
                      name="ageCalcPoint"
                      value={opt.value}
                      checked={bookingRules.ageCalculationPoint === opt.value}
                      onChange={() => updateBookingRule("ageCalculationPoint", opt.value)}
                      className={styles.radioInput}
                    />
                    <div className={styles.radioCardContent}>
                      <div className={styles.radioCardTitle}>{opt.label}</div>
                      <div className={styles.radioCardDesc}>{opt.desc}</div>
                    </div>
                    {bookingRules.ageCalculationPoint === opt.value && (
                      <span className={styles.radioCardCheck}>✓</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section: Document Rules                                         */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "documents" && (
        <div className={styles.sectionContent}>
          <SectionHeader
            icon="🛂"
            title="Travel Document Requirements"
            desc="Define passport and documentation requirements per traveler type. These rules generate warnings in the booking flow when documents are missing or expiring."
          />

          {/* ── Per-type document rules ── */}
          {docRules.map(rule => {
            const ageRule = ageRules.find(r => r.id === rule.id);
            return (
              <div key={rule.id} className={styles.docRuleCard}
                style={{ "--rcolor": ageRule?.color, "--rbg": ageRule?.bg, "--rborder": ageRule?.border }}>

                <div className={styles.docRuleHeader}>
                  <span className={styles.docRuleTitle}
                    style={{ color: ageRule?.color }}>
                    {rule.label}
                  </span>
                  <span className={styles.docRuleRange}>
                    {ageRule?.minAge}
                    {ageRule?.maxAge !== null ? ` – ${ageRule?.maxAge}` : "+"} years
                  </span>
                </div>

                <div className={styles.docRuleBody}>
                  {/* Passport Required Toggle */}
                  <div className={styles.docRuleRow}>
                    <div className={styles.docRuleRowLeft}>
                      <div className={styles.docRuleRowTitle}>Passport Required</div>
                      <div className={styles.docRuleRowDesc}>
                        Booking will be blocked without a valid passport number on file.
                      </div>
                    </div>
                    <Toggle
                      checked={rule.passportRequired}
                      onChange={v => updateDocRule(rule.id, "passportRequired", v)}
                    />
                  </div>

                  {/* Passport Recommended Toggle */}
                  <div className={styles.docRuleRow}>
                    <div className={styles.docRuleRowLeft}>
                      <div className={styles.docRuleRowTitle}>Passport Recommended</div>
                      <div className={styles.docRuleRowDesc}>
                        A warning will appear if passport is not provided, but booking will not be blocked.
                      </div>
                    </div>
                    <Toggle
                      checked={rule.passportRecommended}
                      onChange={v => updateDocRule(rule.id, "passportRecommended", v)}
                      disabled={rule.passportRequired}
                    />
                  </div>

                  {/* Min Expiry Months */}
                  <div className={styles.docRuleRow}>
                    <div className={styles.docRuleRowLeft}>
                      <div className={styles.docRuleRowTitle}>Minimum Validity Beyond Return Date</div>
                      <div className={styles.docRuleRowDesc}>
                        Passport must be valid for this many months after the return flight date.
                      </div>
                    </div>
                    <div className={styles.docNumericInput}>
                      <button onClick={() => updateDocRule(rule.id, "minExpiryMonths", Math.max(0, rule.minExpiryMonths - 1))}>−</button>
                      <span>{rule.minExpiryMonths}</span>
                      <button onClick={() => updateDocRule(rule.id, "minExpiryMonths", Math.min(24, rule.minExpiryMonths + 1))}>+</button>
                      <span className={styles.docNumericUnit}>months</span>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className={styles.docRuleNotesRow}>
                    <label>Notes (shown to agents)</label>
                    <textarea
                      className={styles.docNotesInput}
                      value={rule.notes}
                      rows={2}
                      onChange={e => updateDocRule(rule.id, "notes", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Global Expiry Warning Setting ── */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <div className={styles.cardHeader}><span>⚠️</span><h3>Expiry Warning Threshold</h3></div>
            <div className={styles.cardBody}>
              <p className={styles.cardDesc}>
                Traveler profiles with passports expiring within this period will show a warning badge
                throughout the system (traveler list, detail page, and booking flow).
              </p>
              <div className={styles.thresholdRow}>
                <label>Show warning when passport expires within</label>
                <div className={styles.docNumericInput}>
                  <button onClick={() => updateBookingRule("passportExpiryWarningDays", Math.max(30, bookingRules.passportExpiryWarningDays - 30))}>−</button>
                  <span>{bookingRules.passportExpiryWarningDays}</span>
                  <button onClick={() => updateBookingRule("passportExpiryWarningDays", Math.min(365, bookingRules.passportExpiryWarningDays + 30))}>+</button>
                  <span className={styles.docNumericUnit}>days</span>
                </div>
                <span className={styles.thresholdNote}>({Math.round(bookingRules.passportExpiryWarningDays / 30)} months)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section: Booking Constraints                                    */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "booking" && (
        <div className={styles.sectionContent}>
          <SectionHeader
            icon="📋"
            title="Booking Constraints"
            desc="Rules that govern how travelers of different types can be combined within a single booking. These apply to the Add Booking flow."
          />

          {/* ── Infant Rules ── */}
          <div className={styles.constraintGroup}>
            <div className={styles.constraintGroupTitle}>
              <span>👶</span> Infant Rules
            </div>

            <div className={styles.constraintCard}>
              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Infant Must Travel with Adult</div>
                  <div className={styles.constraintDesc}>
                    A booking containing an infant must include at least one adult. Infant-only bookings are not permitted.
                  </div>
                </div>
                <Toggle
                  checked={bookingRules.infantsMustHaveAdult}
                  onChange={v => updateBookingRule("infantsMustHaveAdult", v)}
                />
              </div>

              <div className={styles.constraintDivider} />

              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Maximum Infants per Adult</div>
                  <div className={styles.constraintDesc}>
                    How many infants can be assigned to a single adult traveler in one booking.
                    Most airlines allow a maximum of 1.
                  </div>
                </div>
                <div className={styles.counterInput}>
                  <button onClick={() => updateBookingRule("maxInfantsPerAdult", Math.max(1, bookingRules.maxInfantsPerAdult - 1))}>−</button>
                  <span>{bookingRules.maxInfantsPerAdult}</span>
                  <button onClick={() => updateBookingRule("maxInfantsPerAdult", Math.min(4, bookingRules.maxInfantsPerAdult + 1))}>+</button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Child Rules ── */}
          <div className={styles.constraintGroup}>
            <div className={styles.constraintGroupTitle}>
              <span>👦</span> Child Rules
            </div>

            <div className={styles.constraintCard}>
              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Children Must Travel with Adult</div>
                  <div className={styles.constraintDesc}>
                    A booking containing children must include at least one adult.
                    Unaccompanied minor (UM) bookings require special handling.
                  </div>
                </div>
                <Toggle
                  checked={bookingRules.childrenMustHaveAdult}
                  onChange={v => updateBookingRule("childrenMustHaveAdult", v)}
                />
              </div>

              <div className={styles.constraintDivider} />

              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Maximum Unaccompanied Children per Booking</div>
                  <div className={styles.constraintDesc}>
                    If set to 0, child-only bookings are completely blocked. Set higher to allow UM bookings with a cap.
                  </div>
                </div>
                <div className={styles.counterInput}>
                  <button onClick={() => updateBookingRule("maxChildrenUnaccompanied", Math.max(0, bookingRules.maxChildrenUnaccompanied - 1))}>−</button>
                  <span>{bookingRules.maxChildrenUnaccompanied}</span>
                  <button onClick={() => updateBookingRule("maxChildrenUnaccompanied", Math.min(9, bookingRules.maxChildrenUnaccompanied + 1))}>+</button>
                </div>
              </div>
            </div>
          </div>

          {/* ── General Booking Rules ── */}
          <div className={styles.constraintGroup}>
            <div className={styles.constraintGroupTitle}>
              <span>📋</span> General Booking Limits
            </div>

            <div className={styles.constraintCard}>
              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Minimum Adults per Booking</div>
                  <div className={styles.constraintDesc}>
                    Every booking must include at least this many adult travelers.
                  </div>
                </div>
                <div className={styles.counterInput}>
                  <button onClick={() => updateBookingRule("minAdultsPerBooking", Math.max(0, bookingRules.minAdultsPerBooking - 1))}>−</button>
                  <span>{bookingRules.minAdultsPerBooking}</span>
                  <button onClick={() => updateBookingRule("minAdultsPerBooking", Math.min(5, bookingRules.minAdultsPerBooking + 1))}>+</button>
                </div>
              </div>

              <div className={styles.constraintDivider} />

              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Maximum Travelers per Booking</div>
                  <div className={styles.constraintDesc}>
                    The total number of travelers (adults + children + infants) allowed in a single booking.
                    Most GDS systems cap at 9.
                  </div>
                </div>
                <div className={styles.counterInput}>
                  <button onClick={() => updateBookingRule("maxTravelersPerBooking", Math.max(1, bookingRules.maxTravelersPerBooking - 1))}>−</button>
                  <span>{bookingRules.maxTravelersPerBooking}</span>
                  <button onClick={() => updateBookingRule("maxTravelersPerBooking", Math.min(20, bookingRules.maxTravelersPerBooking + 1))}>+</button>
                </div>
              </div>

              <div className={styles.constraintDivider} />

              <div className={styles.constraintRow}>
                <div className={styles.constraintLeft}>
                  <div className={styles.constraintTitle}>Age Verification Required</div>
                  <div className={styles.constraintDesc}>
                    Agents must confirm that traveler date of birth has been verified against a valid document before confirming a booking.
                  </div>
                </div>
                <Toggle
                  checked={bookingRules.ageVerificationRequired}
                  onChange={v => updateBookingRule("ageVerificationRequired", v)}
                />
              </div>
            </div>
          </div>

          {/* ── Rules Summary Table ── */}
          <div className={styles.card} style={{ marginTop: 4 }}>
            <div className={styles.cardHeader}><span>📊</span><h3>Active Rules Summary</h3></div>
            <table className={styles.summaryTable}>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Infant must travel with adult",       value: bookingRules.infantsMustHaveAdult      ? "Yes" : "No", on: bookingRules.infantsMustHaveAdult },
                  { label: "Max infants per adult",               value: `${bookingRules.maxInfantsPerAdult} infant${bookingRules.maxInfantsPerAdult !== 1 ? "s" : ""}`, on: true },
                  { label: "Children must travel with adult",     value: bookingRules.childrenMustHaveAdult     ? "Yes" : "No", on: bookingRules.childrenMustHaveAdult },
                  { label: "Max unaccompanied children",          value: bookingRules.maxChildrenUnaccompanied === 0 ? "Blocked" : String(bookingRules.maxChildrenUnaccompanied), on: bookingRules.maxChildrenUnaccompanied > 0 },
                  { label: "Min adults per booking",              value: String(bookingRules.minAdultsPerBooking), on: true },
                  { label: "Max travelers per booking",           value: String(bookingRules.maxTravelersPerBooking), on: true },
                  { label: "Age verification required",           value: bookingRules.ageVerificationRequired   ? "Yes" : "No", on: bookingRules.ageVerificationRequired },
                  { label: "Age calculation reference",           value: bookingRules.ageCalculationPoint === "outbound" ? "Outbound flight date" : "Booking date", on: true },
                  { label: "Passport expiry warning threshold",   value: `${bookingRules.passportExpiryWarningDays} days`, on: true },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className={styles.summaryLabel}>{row.label}</td>
                    <td className={styles.summaryValue}>{row.value}</td>
                    <td>
                      <span className={`${styles.summaryStatus} ${row.on ? styles.summaryOn : styles.summaryOff}`}>
                        {row.on ? "● Active" : "○ Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section: Age Simulator                                          */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "simulator" && (
        <div className={styles.sectionContent}>
          <SectionHeader
            icon="⚡"
            title="Age Simulator"
            desc="Test how a traveler would be classified based on your current rules. Enter a date of birth and a reference date to see the result instantly."
          />

          <div className={styles.simulatorLayout}>

            {/* ── Input Panel ── */}
            <div className={styles.simulatorInputs}>
              <div className={styles.card}>
                <div className={styles.cardHeader}><span>🔢</span><h3>Simulation Inputs</h3></div>
                <div className={styles.cardBody}>

                  <div className={styles.simField}>
                    <label className={styles.simLabel}>Traveler Date of Birth <span className={styles.req}>*</span></label>
                    <input
                      type="date"
                      className={styles.simInput}
                      value={simDob}
                      onChange={e => setSimDob(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  <div className={styles.simField}>
                    <label className={styles.simLabel}>
                      Reference Date
                      <span className={styles.simLabelSub}>(outbound flight date)</span>
                    </label>
                    <input
                      type="date"
                      className={styles.simInput}
                      value={simDate}
                      onChange={e => setSimDate(e.target.value)}
                    />
                    <div className={styles.simHint}>Leave as today to simulate current classification.</div>
                  </div>

                  {/* Quick DOB presets */}
                  <div className={styles.simPresets}>
                    <div className={styles.simPresetsLabel}>Quick presets:</div>
                    <div className={styles.simPresetBtns}>
                      {[
                        { label: "Newborn (3mo)", months: 3 },
                        { label: "Infant (1yr)",  months: 14 },
                        { label: "Child (6yr)",   months: 72 },
                        { label: "Child (11yr)",  months: 132 },
                        { label: "Adult (30yr)",  months: 360 },
                      ].map(p => {
                        const d = new Date();
                        d.setMonth(d.getMonth() - p.months);
                        return (
                          <button
                            key={p.label}
                            className={styles.simPresetBtn}
                            onClick={() => setSimDob(d.toISOString().split("T")[0])}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Result Panel ── */}
            <div className={styles.simulatorResult}>
              {!simResult ? (
                <div className={styles.simResultEmpty}>
                  <div className={styles.simEmptyIcon}>⚡</div>
                  <div className={styles.simEmptyTitle}>Enter a date of birth to simulate</div>
                  <div className={styles.simEmptyDesc}>The result will appear here instantly based on your current rules.</div>
                </div>
              ) : (
                <div
                  className={styles.simResultCard}
                  style={{
                    "--rcolor": simResult.type?.color,
                    "--rbg": simResult.type?.bg,
                    "--rborder": simResult.type?.border,
                  }}
                >
                  <div className={styles.simResultIcon}>{simResult.type?.icon}</div>

                  <div
                    className={styles.simResultBadge}
                    style={{
                      background: simResult.type?.bg,
                      color: simResult.type?.color,
                      border: `2px solid ${simResult.type?.border}`,
                    }}
                  >
                    {simResult.type?.label ?? "Unknown"}
                  </div>

                  <div className={styles.simResultCode}>
                    Code: <strong>{simResult.type?.shortCode ?? "—"}</strong>
                  </div>

                  <div className={styles.simResultAge}>
                    Age: <strong>{simResult.age} year{simResult.age !== 1 ? "s" : ""} old</strong>
                  </div>

                  <div className={styles.simResultRange}>
                    Range:{" "}
                    <strong>
                      {simResult.type?.minAge}
                      {simResult.type?.maxAge !== null ? ` – ${simResult.type?.maxAge}` : "+"} years
                    </strong>
                  </div>

                  <div className={styles.simResultBreakdown}>
                    <div className={styles.simBreakdownTitle}>Calculation breakdown</div>
                    <div className={styles.simBreakdownRow}>
                      <span>Date of birth:</span><strong>{new Date(simDob).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}</strong>
                    </div>
                    <div className={styles.simBreakdownRow}>
                      <span>Reference date:</span><strong>{new Date(simDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}</strong>
                    </div>
                    <div className={styles.simBreakdownRow}>
                      <span>Age on reference date:</span><strong>{simResult.age} year{simResult.age !== 1 ? "s" : ""}</strong>
                    </div>
                    <div className={styles.simBreakdownRow}>
                      <span>Classification rule:</span><strong>{simResult.type?.description}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── All Rules Reference ── */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <div className={styles.cardHeader}><span>📖</span><h3>Current Classification Rules Reference</h3></div>
            <div className={styles.rulesReference}>
              {ageRules.map(rule => (
                <div key={rule.id} className={styles.refRow}
                  style={{ borderLeft: `3px solid ${rule.color}` }}>
                  <span className={styles.refIcon}>{rule.icon}</span>
                  <div className={styles.refInfo}>
                    <div className={styles.refTitle} style={{ color: rule.color }}>
                      {rule.label} ({rule.shortCode})
                    </div>
                    <div className={styles.refDesc}>{rule.description}</div>
                  </div>
                  <div className={styles.refRange}
                    style={{ background: rule.bg, color: rule.color, border: `1px solid ${rule.border}` }}>
                    {rule.minAge}
                    {rule.maxAge !== null ? ` – ${rule.maxAge}` : "+"} yrs
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TravelerRules;